import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  AreaChart,
  Area,
  YAxis,
  ResponsiveContainer
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedDashboard = () => {
  const [searchParams] = useSearchParams();

  const bg = searchParams.get('bg') || '#0a0a0a';
  const showCharts = searchParams.get('chart') !== 'false';
  const leaderboardLimit = parseInt(searchParams.get('limit')) || 50;
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;
  const leaderboardWidth = searchParams.get('lbWidth') || '25%';

  const [stocks, setStocks] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);
  const prevRanksRef = useRef({});

  const fetchData = async () => {
    try {
      const [stocksRes, lbRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/stocks`),
        fetch(`${API_BASE_URL}/api/leaderboard?limit=${leaderboardLimit}`),
      ]);
      if (stocksRes.ok) {
        const data = await stocksRes.json();
        setStocks(data.map(stock => ({
          ...stock,
          changePercent: stock.basePrice
            ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100
            : 0,
          chartData: (stock.history || []).slice(-20).map((h, i) => ({
            index: i,
            price: h.price || h.close
          }))
        })));
      }
      if (lbRes.ok) {
        const data = await lbRes.json();
        updateLeaderboard(data.slice(0, leaderboardLimit));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeaderboard = (newData) => {
    const currentRanks = {};
    newData.forEach((user, index) => {
      const prevRank = prevRanksRef.current[user.id];
      currentRanks[user.id] = index + 1;
      user.rankChange = prevRank ? prevRank - (index + 1) : 0;
    });
    prevRanksRef.current = currentRanks;
    setLeaderboard(newData);
  };

  useEffect(() => {
    fetchData();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });

    socketRef.current.on('stock-updates', (updates) => {
      if (!Array.isArray(updates)) return;
      setStocks(prev => prev.map(stock => {
        const update = updates.find(u => u.id === stock.id || u.stockId === stock.id);
        if (update) {
          const newPrice = update.currentPrice || update.price;
          const newChartData = [...(stock.chartData || [])];
          newChartData.push({ index: newChartData.length, price: newPrice });
          return {
            ...stock,
            currentPrice: newPrice,
            changePercent: stock.basePrice
              ? ((newPrice - stock.basePrice) / stock.basePrice) * 100
              : stock.changePercent,
            chartData: newChartData.slice(-30)
          };
        }
        return stock;
      }));
    });

    socketRef.current.on('leaderboard-update', (data) => {
      const list = Array.isArray(data) ? data : data?.leaderboard;
      if (Array.isArray(list)) {
        updateLeaderboard(list.slice(0, leaderboardLimit));
      }
    });

    const interval = setInterval(fetchData, refreshInterval);
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [refreshInterval, leaderboardLimit]);

  const getHeatmapColor = (changePercent) => {
    const max = 10;
    const t = Math.max(-1, Math.min(1, changePercent / max));
    const absT = Math.abs(t);
    const nr = 40, ng = 44, nb = 58;
    if (t >= 0) {
      return `rgb(${Math.round(nr + (0 - nr) * absT)}, ${Math.round(ng + (180 - ng) * absT)}, ${Math.round(nb + (60 - nb) * absT)})`;
    } else {
      return `rgb(${Math.round(nr + (220 - nr) * absT)}, ${Math.round(ng + (40 - ng) * absT)}, ${Math.round(nb + (40 - nb) * absT)})`;
    }
  };

  const MiniSparkline = ({ data, isPositive, stockId }) => {
    if (!data || data.length < 2) return null;
    const color = isPositive ? '#00ff88' : '#ff4444';
    const prices = data.map(d => d.price).filter(p => p != null && !isNaN(p) && p > 0);
    if (prices.length === 0) return null;
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const avgPrice = (minPrice + maxPrice) / 2;
    const minRange = avgPrice * 0.05;
    const actualRange = Math.max(priceRange, minRange);
    const padding = actualRange * 0.2;
    const domain = [
      Math.max(0, avgPrice - actualRange / 2 - padding),
      avgPrice + actualRange / 2 + padding
    ];

    return (
      <ResponsiveContainer width="100%" height={24}>
        <AreaChart data={data} margin={{ top: 1, right: 0, bottom: 1, left: 0 }}>
          <defs>
            <linearGradient id={`dg-${stockId}-${isPositive ? 'p' : 'n'}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <YAxis domain={domain} hide />
          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            strokeWidth={1}
            fill={`url(#dg-${stockId}-${isPositive ? 'p' : 'n'})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  const getRankBadge = (rank) => {
    switch (rank) {
      case 1: return '1';
      case 2: return '2';
      case 3: return '3';
      default: return `${rank}`;
    }
  };

  if (loading) {
    return (
      <div className="embed-dashboard" style={{ background: bg }}>
        <div className="embed-loading">Loading dashboard...</div>
      </div>
    );
  }

  // Auto-calculate grid
  const stockCount = stocks.length;
  const gridCols = showCharts
    ? Math.ceil(Math.sqrt(stockCount * 2))
    : Math.ceil(Math.sqrt(stockCount * 1.5));
  const gridRows = Math.ceil(stockCount / gridCols);

  return (
    <div
      className="embed-dashboard"
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {/* Market Overview — 3/4 */}
      <div className="dashboard-market" style={{ width: `calc(100% - ${leaderboardWidth})` }}>
        <div
          className="dashboard-market-grid"
          style={{
            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
            gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          }}
        >
          {stocks.map((stock) => {
            const isPositive = stock.changePercent >= 0;
            return (
              <div
                key={stock.id}
                className="dashboard-stock-card"
                style={{ background: getHeatmapColor(stock.changePercent) }}
              >
                <div className="dsc-header">
                  <span className="dsc-ticker">{stock.ticker}</span>
                  <span className={`dsc-change ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}{stock.changePercent.toFixed(1)}%
                  </span>
                </div>
                <div className="dsc-price">{formatCurrency(stock.currentPrice)}</div>
                {showCharts && stock.chartData && stock.chartData.length > 1 && (
                  <div className="dsc-chart">
                    <MiniSparkline data={stock.chartData} isPositive={isPositive} stockId={stock.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leaderboard — 1/4 */}
      <div className="dashboard-leaderboard" style={{ width: leaderboardWidth }}>
        <div className="dlb-header">LEADERBOARD</div>
        <div className="dlb-list">
          {leaderboard.length === 0 && (
            <div className="embed-waiting">Waiting for traders...</div>
          )}
          {leaderboard.map((user, index) => {
            const rank = index + 1;
            const pnl = user.totalValue - 10000;
            const pnlPercent = user.percentGain || 0;
            const isPositive = pnl >= 0;
            const isTop3 = rank <= 3;

            return (
              <div key={user.id} className={`dlb-item ${isTop3 ? `dlb-top-${rank}` : ''}`}>
                <div className={`dlb-rank ${isTop3 ? `dlb-rank-${rank}` : ''}`}>
                  {getRankBadge(rank)}
                </div>
                <div className="dlb-info">
                  <span className="dlb-name">
                    {user.username || user.displayName || user.shortId || `Trader ${user.id?.slice(-4)}`}
                  </span>
                </div>
                <div className="dlb-value">
                  <span className="dlb-total">{formatCurrency(user.totalValue)}</span>
                  <span className={`dlb-pnl ${isPositive ? 'positive' : 'negative'}`}>
                    {isPositive ? '+' : ''}{pnlPercent.toFixed(1)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmbedDashboard;
